import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import Navbar from './components/Navbar/Navbar';
import ReactGA from 'react-ga';
import ScrollToTop from './components/ScrollToTop';
import { UNSAFE_NavigationContext } from 'react-router-dom';

// Lazy load components
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Resume = lazy(() => import('./pages/Resume'));
const Contact = lazy(() => import('./pages/Contact'));
const Blog = lazy(() => import('./pages/Blog'));

// Cross-browser compatible requestIdleCallback
const requestIdleCallbackCompat =
  typeof window !== 'undefined' && window.requestIdleCallback
    ? window.requestIdleCallback
    : function (cb) {
        return setTimeout(() => {
          const start = Date.now();
          cb({
            didTimeout: false,
            timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
          });
        }, 1);
      };

// Loading component
const LoadingFallback = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh' 
    }}
  >
    <CircularProgress />
  </Box>
);

// Enable React Router v7 startTransition
UNSAFE_NavigationContext.future = {
  v7_startTransition: true
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#64FFDA', // Modern mint color
      light: '#9EFFEB',
      dark: '#14B8A6',
    },
    secondary: {
      main: '#F0DB4F', // JavaScript yellow for accent
      light: '#F4E584',
      dark: '#C7B42C',
    },
    background: {
      default: '#0A192F', // Deep navy background
      paper: '#112240',
    },
    text: {
      primary: '#CCD6F6',
      secondary: '#8892B0',
    },
  },
  typography: {
    fontFamily: "'Fira Code', 'Roboto Mono', monospace",
    htmlFontSize: 16,
    fontSize: 14,
    h1: {
      fontWeight: 700,
      fontSize: {
        xs: '2rem',      // 32px
        sm: '2.75rem',   // 44px
        md: '3.25rem',   // 52px
        lg: '3.75rem',   // 60px
      },
      letterSpacing: '-0.01562em',
      color: '#CCD6F6',
      lineHeight: {
        xs: 1.2,
        sm: 1.1,
        md: 1.1,
      },
    },
    h2: {
      fontWeight: 600,
      fontSize: {
        xs: '1.75rem',   // 28px
        sm: '2.25rem',   // 36px
        md: '2.5rem',    // 40px
        lg: '2.75rem',   // 44px
      },
      letterSpacing: '-0.00833em',
      color: '#CCD6F6',
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 600,
      fontSize: {
        xs: '1.5rem',    // 24px
        sm: '1.75rem',   // 28px
        md: '2rem',      // 32px
        lg: '2.25rem',   // 36px
      },
      letterSpacing: '0em',
      color: '#CCD6F6',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: {
        xs: '1.25rem',   // 20px
        sm: '1.5rem',    // 24px
        md: '1.75rem',   // 28px
        lg: '2rem',      // 32px
      },
      letterSpacing: '0.00735em',
      color: '#CCD6F6',
      lineHeight: 1.4,
    },
    body1: {
      fontSize: {
        xs: '0.875rem',  // 14px
        sm: '0.9375rem', // 15px
        md: '1rem',      // 16px
      },
      letterSpacing: '0.00938em',
      color: '#8892B0',
      lineHeight: 1.75,
    },
    body2: {
      fontSize: {
        xs: '0.8125rem', // 13px
        sm: '0.875rem',  // 14px
        md: '0.9375rem', // 15px
      },
      letterSpacing: '0.01071em',
      color: '#8892B0',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: {
            xs: '0.875rem',  // 14px
            sm: '0.9375rem', // 15px
            md: '1rem',      // 16px
          },
          fontWeight: 500,
          borderRadius: 8,
          padding: {
            xs: '0.625rem 1.25rem',
            sm: '0.75rem 1.5rem',
            md: '0.875rem 1.75rem',
          },
          minWidth: {
            xs: '120px',
            sm: '140px',
            md: '160px',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
        sizeLarge: {
          fontSize: {
            xs: '0.9375rem', // 15px
            sm: '1rem',      // 16px
            md: '1.125rem',  // 18px
          },
          padding: {
            xs: '0.75rem 1.5rem',
            sm: '0.875rem 1.75rem',
            md: '1rem 2rem',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#112240',
          '&:hover': {
            backgroundColor: '#1A2C4E',
          },
          transition: 'all 0.3s ease-in-out',
          padding: {
            xs: '1.25rem',
            sm: '1.5rem',
            md: '2rem',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(10, 25, 47, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: 'none',
          borderBottom: '1px solid rgba(100, 255, 218, 0.1)',
          padding: {
            xs: '0.5rem',
            sm: '0.625rem 1rem',
            md: '0.75rem 1.5rem',
          },
          minHeight: {
            xs: '56px',
            sm: '64px',
            md: '72px',
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: {
            xs: '1rem',
            sm: '1.5rem',
            md: '2rem',
            lg: '3rem',
          },
          paddingRight: {
            xs: '1rem',
            sm: '1.5rem',
            md: '2rem',
            lg: '3rem',
          },
          maxWidth: {
            xs: '100%',
            sm: '600px',
            md: '960px',
            lg: '1280px',
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        container: {
          spacing: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          padding: {
            xs: '1rem',
            sm: '1.5rem',
            md: '2rem',
          },
        },
      },
    },
  },
  spacing: (factor) => `${0.25 * factor}rem`, // Base spacing unit of 4px (0.25rem)
});

function App() {
  React.useEffect(() => {
    // Initialize Google Analytics
    if (process.env.REACT_APP_GA_TRACKING_CODE) {
      ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_CODE);
      ReactGA.pageview(window.location.pathname + window.location.search);
    }

    // Preload critical components
    const preloadComponents = () => {
      const preloadHome = () => import('./pages/Home');
      const preloadAbout = () => import('./pages/About');
      requestIdleCallbackCompat(() => {
        preloadHome();
        preloadAbout();
      });
    };

    preloadComponents();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ScrollToTop />
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        minHeight: '100vh',
        backgroundColor: 'background.default',
        color: 'text.primary',
      }}>
        <Navbar />
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1,
            py: 3,
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App; 